import { useT } from '../lib/i18n';
import type { Accommodation, AttributeDef } from '../lib/api';
import {
  activeFilters, enumOptions, numericRange, type Filter, type FilterState,
} from '../lib/filters';

/**
 * Filters, one control per declared kind.
 *
 * The API sends attribute DEFINITIONS, with a `kind` that is one of four. Each
 * gets the control that kind deserves and none of them gets a text box:
 *
 *   number   two bounds, seeded from the range the park's own stock spans, so
 *            "at least" cannot be pushed past the largest thing that exists
 *   boolean  one toggle, one-sided — "only with this" is a request a guest
 *            makes and "only without this" is not
 *   enum     the declared options, in the operator's order, as multi-select
 *            chips; a filter reads as OR within an attribute and AND across
 *   text     substring, because nothing better is knowable about free text
 *
 * A kind that arrives here and is not one of the four renders nothing at all,
 * rather than falling back to a control that filters on the wrong thing. The
 * API's own rule is that an attribute nobody declared is dropped; this is the
 * same instinct one layer out.
 *
 * Changing anything here costs no request. Availability across the filtered set
 * is recomputed from held data by `combinePeriods` — that closure property
 * (§3.3) is what makes a filterable calendar possible at all, and it is why
 * this component is allowed to be as chatty as a guest wants.
 */
export function Filters({ defs, accommodations, state, onChange }: {
  defs: readonly AttributeDef[];
  /** The unfiltered set: bounds and options come from what the park has. */
  accommodations: readonly Accommodation[];
  state: FilterState;
  onChange: (next: FilterState) => void;
}) {
  const t = useT();
  const active = activeFilters(state);

  function set(id: string, filter: Filter | null) {
    const next = { ...state };
    if (filter === null) delete next[id];
    else next[id] = filter;
    onChange(next);
  }

  if (defs.length === 0) return null;

  return (
    <div className="pw-filters">
      {active.length > 0 && (
        <div className="pw-row">
          <span className="pw-tag pw-tag-info">
            {t('filters.active', { count: active.length })}
          </span>
          <button type="button" className="pw-btn pw-btn-ghost" onClick={() => onChange({})}>
            {t('filters.clear')}
          </button>
        </div>
      )}

      {defs.map((def) => (
        <div className="pw-filter" key={def.id}>
          {/* The label is operator data and is shown exactly as typed. */}
          <div className="pw-label" id={`pw-f-${def.id}`}>
            {def.label}
            {def.unit && <span className="pw-label-hint">{def.unit}</span>}
          </div>
          <Control
            def={def}
            accommodations={accommodations}
            filter={state[def.id]}
            onChange={(f) => set(def.id, f)}
          />
        </div>
      ))}
    </div>
  );
}

function Control({ def, accommodations, filter, onChange }: {
  def: AttributeDef;
  accommodations: readonly Accommodation[];
  filter: Filter | undefined;
  onChange: (filter: Filter | null) => void;
}) {
  const t = useT();
  const labelId = `pw-f-${def.id}`;

  if (def.kind === 'number') {
    const range = numericRange(accommodations, def.id);
    const current = filter?.kind === 'number' ? filter : { kind: 'number' as const };
    const write = (patch: { min?: number; max?: number }) => {
      const next = { ...current, ...patch };
      const empty = next.min === undefined && next.max === undefined;
      onChange(empty ? null : next);
    };
    return (
      <div className="pw-range">
        <label className="pw-field">
          <span className="pw-label-hint">{t('filters.min')}</span>
          <input
            className="pw-input pw-num"
            type="number"
            inputMode="numeric"
            min={range?.min}
            max={range?.max}
            value={current.min ?? ''}
            onChange={(e) => write({
              min: e.target.value === '' ? undefined : Number(e.target.value),
            })}
          />
        </label>
        <label className="pw-field">
          <span className="pw-label-hint">{t('filters.max')}</span>
          <input
            className="pw-input pw-num"
            type="number"
            inputMode="numeric"
            min={range?.min}
            max={range?.max}
            value={current.max ?? ''}
            onChange={(e) => write({
              max: e.target.value === '' ? undefined : Number(e.target.value),
            })}
          />
        </label>
      </div>
    );
  }

  if (def.kind === 'boolean') {
    const on = filter?.kind === 'boolean' && filter.value;
    return (
      <label className="pw-check">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => onChange(e.target.checked ? { kind: 'boolean', value: true } : null)}
        />
        {t('filters.only')}
      </label>
    );
  }

  if (def.kind === 'enum') {
    const options = enumOptions(accommodations, def);
    const chosen = filter?.kind === 'enum' ? filter.values : [];
    return (
      <div className="pw-chips" role="group" aria-labelledby={labelId}>
        {options.map((option) => {
          const on = chosen.includes(option);
          return (
            <button
              key={option}
              type="button"
              className="pw-chip"
              aria-pressed={on}
              onClick={() => {
                const next = on ? chosen.filter((v) => v !== option) : [...chosen, option];
                onChange(next.length ? { kind: 'enum', values: next } : null);
              }}
            >
              {/* An enum value is operator data; it is shown, never translated. */}
              {option}
            </button>
          );
        })}
      </div>
    );
  }

  if (def.kind === 'text') {
    const value = filter?.kind === 'text' ? filter.contains : '';
    return (
      <input
        className="pw-input"
        type="search"
        aria-labelledby={labelId}
        placeholder={t('filters.contains')}
        value={value}
        onChange={(e) => onChange(e.target.value ? { kind: 'text', contains: e.target.value } : null)}
      />
    );
  }

  // A kind this build has never heard of. Nothing is better than a control that
  // claims to filter and does not.
  return null;
}
